import React, { useEffect, useMemo, useState } from "react";
import api from "../../components/axiosComp";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";

// ---------------- helpers ----------------
const clampNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (n) => {
  const x = clampNumber(n);
  return x.toLocaleString("en-IN");
};

const formatCurrency = (amount, currency = "INR") => {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString("en-IN")}`;
  }
};

const formatPct = (v) => {
  if (v === null || v === undefined) return "—";
  const num = Number(v);
  if (!Number.isFinite(num)) return "—";
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toFixed(1)}%`;
};

const isUp = (v) => Number(v) >= 0;

const formatMoney = (n, currency = "INR") => {
  const value = Number(n) || 0;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString("en-IN")}`;
  }
};

const ArrowIcon = ({ up = true }) => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "inline-block", verticalAlign: "middle", flex: "0 0 auto" }}
      aria-hidden="true"
    >
      {up ? (
        <path
          d="M12 5l7 7-1.41 1.41L13 8.83V20h-2V8.83L6.41 13.41 5 12l7-7z"
          fill="currentColor"
        />
      ) : (
        <path
          d="M12 19l-7-7 1.41-1.41L11 15.17V4h2v11.17l4.59-4.58L19 12l-7 7z"
          fill="currentColor"
        />
      )}
    </svg>
  );
};

function getVenueAdminName() {
  try {
    const ctx = JSON.parse(localStorage.getItem("va_ctx") || "{}");
    return String(ctx?.name || ctx?.fullName || "").trim() || "Venue Admin";
  } catch (_) {
    return "Venue Admin";
  }
}

// ---------------- component ----------------
const Dashboard = () => {
  const [range, setRange] = useState("thisWeek");
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const [metrics, setMetrics] = useState({
    currency: "INR",
    totalRevenue: { value: 0, deltaPct: 0 },
    upcomingBookings: { value: 0, todayCount: 0 },
    venueUtilization: { valuePct: 0, deltaPctPoints: 0 },
    newCustomers: { value: 0, deltaPct: 0 },
  });

  const [rev7, setRev7] = useState({ currency: "INR", items: [] });
  const [venuePerf, setVenuePerf] = useState([]);
  const [upcomingRows, setUpcomingRows] = useState([]);

  const maxBarIndex = useMemo(() => {
    const items = Array.isArray(rev7?.items) ? rev7.items : [];
    if (!items.length) return -1;

    let maxIdx = 0;
    let maxVal = Number(items[0]?.amount) || 0;

    for (let i = 1; i < items.length; i += 1) {
      const v = Number(items[i]?.amount) || 0;
      if (v > maxVal) {
        maxVal = v;
        maxIdx = i;
      }
    }
    return maxIdx;
  }, [rev7.items]);

  const subtitle = useMemo(() => {
    if (range === "thisMonth") return "Here’s your business overview for this month.";
    if (range === "thisYear") return "Here’s your business overview for this year.";
    return "Here’s your business overview for this week.";
  }, [range]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErrMsg("");

      try {
        const [overviewRes, rev7Res, perfRes, upcomingRes] = await Promise.all([
          api.get("/api/venue-admin/dashboard/metrics/overview", { params: { range } }),
          api.get("/api/venue-admin/dashboard/revenue/last-7-days", { params: { range } }),
          api.get("/api/venue-admin/dashboard/venue-performance", { params: { range, limit: 4 } }),
          api.get("/api/venue-admin/dashboard/upcoming-bookings", { params: { range, limit: 4 } }),
        ]);

        if (cancelled) return;

        const m = overviewRes.data?.metrics || {};
        setMetrics({
          currency: overviewRes.data?.currency || m.currency || "INR",
          totalRevenue: {
            value: m.totalRevenue?.value ?? 0,
            deltaPct: m.totalRevenue?.deltaPct ?? 0,
          },
          upcomingBookings: {
            value: m.upcomingBookings?.value ?? 0,
            todayCount: m.upcomingBookings?.todayCount ?? 0,
          },
          venueUtilization: {
            valuePct: m.venueUtilization?.valuePct ?? 0,
            deltaPctPoints: m.venueUtilization?.deltaPctPoints ?? 0,
          },
          newCustomers: {
            value: m.newCustomers?.value ?? 0,
            deltaPct: m.newCustomers?.deltaPct ?? 0,
          },
        });

        setRev7({
          currency: rev7Res.data?.currency || overviewRes.data?.currency || "INR",
          items: Array.isArray(rev7Res.data?.items) ? rev7Res.data.items : [],
        });

        setVenuePerf(Array.isArray(perfRes.data?.items) ? perfRes.data.items : []);
        setUpcomingRows(Array.isArray(upcomingRes.data?.items) ? upcomingRes.data.items : []);
      } catch (e) {
        console.error("VenueAdmin Dashboard metrics error:", e);
        if (!cancelled) setErrMsg(e?.response?.data?.message || "Failed to load dashboard metrics.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [range]);

  const revenueUp = isUp(metrics.totalRevenue.deltaPct);
  const utilUp = isUp(metrics.venueUtilization.deltaPctPoints);
  const newCustomersUp = isUp(metrics.newCustomers.deltaPct);

  // styles (layout only; compactness mainly controlled by CSS vars below)
  const styles = {
    page: { padding: 18 }, // smaller overall spacing
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 14,
      flexWrap: "wrap",
      marginBottom: 14,
    },
    rangeWrap: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
    rangeBtn: (active) => ({
      border: active ? "1px solid #b7e2c1" : "1px solid #e5e7eb",
      background: active ? "#dff0df" : "#fff",
      color: active ? "#2f7a3c" : "#374151",
      padding: "8px 12px", // smaller buttons
      borderRadius: 10,
      fontWeight: 700,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      boxShadow: active ? "0 1px 0 rgba(0,0,0,0.02)" : "none",
      userSelect: "none",
      whiteSpace: "nowrap",
      fontSize: 13,
    }),
    caret: { fontSize: 12, opacity: 0.8 },

    cardsRow: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(200px, 1fr))", // slightly narrower
      gap: 14, // tighter
    },
    card: {
      background: "#fff",
      border: "1px solid #eef0f2",
      borderRadius: 14,
      padding: 16, // tighter
      boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
      minHeight: 118, // tighter
      minWidth: 0,
    },
    gridRow2: {
      marginTop: 14,
      display: "grid",
      gridTemplateColumns: "1.8fr 1fr",
      gap: 14,
      alignItems: "stretch",
    },

    cardLabel: { color: "#9ca3af", fontWeight: 700, fontSize: 13 },

    deltaRow: (up) => ({
      marginTop: 10,
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 800,
      fontSize: 13,
      color: up ? "#2f7a3c" : "#dc2626",
      flexWrap: "wrap",
      minWidth: 0,
    }),

    cardTitleRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 },
    cardTitle: { fontSize: 15, fontWeight: 800, margin: 0, color: "#111827" },
    cardSub: { marginTop: 4, color: "#9ca3af", fontWeight: 700, fontSize: 12 },

    chartWrap: { height: 190, marginTop: 8 }, // smaller chart

    perfList: { marginTop: 12, display: "flex", flexDirection: "column", gap: 12 },
    perfRow: { display: "flex", flexDirection: "column", gap: 8 },
    perfTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },
    perfName: { fontWeight: 800, color: "#111827", minWidth: 0, fontSize: 13 },
    perfPct: { fontWeight: 800, color: "#9ca3af", whiteSpace: "nowrap", fontSize: 12 },
    perfTrack: { width: "100%", height: 8, background: "#e5e7eb", borderRadius: 999 },
    perfFill: (pct) => ({
      width: `${Math.max(0, Math.min(100, Number(pct) || 0))}%`,
      height: "100%",
      background: "#2f7a3c",
      borderRadius: 999,
    }),

    tableCardTitle: { fontSize: 15, fontWeight: 800, margin: 0, color: "#111827" },
    tableWrap: { marginTop: 12, borderRadius: 12, overflow: "hidden", border: "1px solid #eef0f2" },
    tableHead: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1.4fr 1.4fr 0.7fr",
      gap: 12,
      padding: "12px 14px",
      background: "#fbf7f7",
      color: "#9ca3af",
      fontWeight: 900,
      fontSize: 11,
      letterSpacing: 0.5,
      minWidth: 720,
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1.4fr 1.4fr 0.7fr",
      gap: 12,
      padding: "12px 14px",
      background: "#fff",
      alignItems: "center",
      borderTop: "1px solid #f1f5f9",
      minWidth: 720,
      fontSize: 13,
    },
    cellStrong: { fontWeight: 800, color: "#111827" },
    cellSoft: { fontWeight: 700, color: "#111827" },
    pill: (status) => {
      const s = String(status || "").toLowerCase();
      const isPaid = s === "paid";
      return {
        justifySelf: "start",
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 10px",
        borderRadius: 999,
        fontWeight: 900,
        fontSize: 11,
        background: isPaid ? "#dff0df" : "#fff3c4",
        color: isPaid ? "#2f7a3c" : "#8a6b12",
        whiteSpace: "nowrap",
      };
    },

    hint: { marginTop: 10, color: "#6b7280", fontSize: 13 },
    error: { marginTop: 10, color: "#dc2626", fontWeight: 700, fontSize: 13 },
  };

  return (
    <div style={styles.page} className="vaDash vaPagePad">
      {/* Compact + Responsive CSS (kept in this file) */}
      <style>{`
        .vaDash * { box-sizing: border-box; }
/* =========================================
   CTRL+F: DASH_MOBILE_SCROLL_FIX
   Reliable mobile scrolling + header visibility
========================================= */

/* Keep page stable; do NOT force body scrolling (modals may rely on scroll lock) */
html, body { height: 100%; }
body { margin: 0; }
/* Default: behave like normal content */
.vaDash{
  min-height: 100%;
  overflow-x: hidden;
}

@media (max-width: 640px){

  /* ✅ Nuclear, but reliable: bypass parent layout scroll locks */
  .vaDash{
    position: fixed;
    inset: 0;                 /* top:0 right:0 bottom:0 left:0 */
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;

    /* Keep content away from notches + bottom bar */
    padding-top: calc(12px + env(safe-area-inset-top));
    padding-bottom: calc(120px + env(safe-area-inset-bottom));
  }

  .vaHeaderRow{
    position: relative;
    top: auto;
    z-index: auto;
  }
}



        /* =========================================
            COMPACT_TOKENS
           Change these values if you want even smaller/larger UI
        ========================================= */
       .vaDash{
  position: relative;
}


        .vaTitle {
          font-size: clamp(var(--title-min), 2.4vw, var(--title-max));
          line-height: 1.12;
          font-weight: 800;
          margin: 0;
          color: #1f2937;
          letter-spacing: -0.2px;
        }

        .vaSubtitle {
          margin-top: 6px;
          color: #9ca3af;
          font-size: clamp(var(--sub-min), 1.3vw, var(--sub-max));
          line-height: 1.45;
        }

        /* KPI value: fixes overflow + makes compact */
        .vaCardValue {
          margin-top: 8px;
          font-weight: 800;
          color: #111827;
          font-size: clamp(var(--kpi-min), 2vw, var(--kpi-max));
          line-height: 1.12;

          /* overflow fix */
          overflow-wrap: anywhere;
          word-break: break-word;
          min-width: 0;
        }

        /* Desktop compactness: slightly tighter when wide */
        @media (min-width: 1280px) {
          .vaCardsRow { gap: 14px !important; }
          .vaRow2 { gap: 14px !important; }
        }

        /* Grid responsiveness */
        @media (max-width: 1200px) {
          .vaCardsRow { grid-template-columns: repeat(2, minmax(200px, 1fr)) !important; }
          .vaRow2 { grid-template-columns: 1fr !important; }
        }

        @media (max-width: 640px) {
          .vaPagePad { padding: 12px !important; }
          .vaHeaderRow { padding-bottom: 8px; }
          .vaCardsRow { grid-template-columns: 1fr !important; gap: 12px !important; }
          .vaCard { padding: 14px !important; border-radius: 14px !important; min-height: unset !important; }
          .vaChartWrap { height: 180px !important; }
          .vaRangeBtn { padding: 8px 10px !important; border-radius: 10px !important; }
        }

        /* Table responsive: scroll only on small screens */
       /* =========================================
   CTRL+F: UPCOMING_TABLE_SCROLL_FIX
   Make table horizontally scrollable on phones reliably
========================================= */
.vaTableScroll{
  width: 100%;
  overflow-x: auto;                  /* horizontal scroll */
  overflow-y: visible;               /* ✅ never become a vertical scroller */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
overscroll-behavior-y: auto;
  scrollbar-gutter: stable both-edges;

  /* ✅ Important: allow vertical scroll to pass through naturally */
touch-action: pan-x; /* allow vertical swipe to scroll the dashboard */
}


.vaTableScrollContent {
  min-width: 720px; /* force wider than phone so scrolling is guaranteed */
}

      `}</style>

      {/* HEADER */}
      <div style={styles.headerRow} className="vaHeaderRow">
        <div style={{ minWidth: 0 }}>
          <h1 className="vaTitle">Welcome, {getVenueAdminName()}!</h1>
          <div className="vaSubtitle">{subtitle}</div>
        </div>

        <div style={styles.rangeWrap}>
          <button
            type="button"
            className="vaRangeBtn"
            style={styles.rangeBtn(range === "thisWeek")}
            onClick={() => setRange("thisWeek")}
          >
            This Week <span style={styles.caret}>▾</span>
          </button>

          <button
            type="button"
            className="vaRangeBtn"
            style={styles.rangeBtn(range === "thisMonth")}
            onClick={() => setRange("thisMonth")}
          >
            This Month
          </button>

          <button
            type="button"
            className="vaRangeBtn"
            style={styles.rangeBtn(range === "thisYear")}
            onClick={() => setRange("thisYear")}
          >
            This Year
          </button>
        </div>
      </div>

      {/* KPI CARDS ROW */}
      <section style={styles.cardsRow} className="vaCardsRow">
        {/* Total Revenue */}
        <article style={styles.card} className="vaCard">
          <div style={styles.cardLabel}>Total Revenue</div>
          <div className="vaCardValue">{formatCurrency(metrics.totalRevenue.value, metrics.currency)}</div>

          <div style={styles.deltaRow(revenueUp)}>
            <ArrowIcon up={revenueUp} />
            {formatPct(metrics.totalRevenue.deltaPct)}
          </div>
        </article>

        {/* Upcoming Bookings */}
        <article style={styles.card} className="vaCard">
          <div style={styles.cardLabel}>Upcoming Bookings</div>
          <div className="vaCardValue">{formatNumber(metrics.upcomingBookings.value)}</div>

          <div style={styles.deltaRow(true)}>
            <ArrowIcon up={true} />
            +{formatNumber(metrics.upcomingBookings.todayCount)} Today
          </div>
        </article>

        {/* Venue Utilization */}
        <article style={styles.card} className="vaCard">
          <div style={styles.cardLabel}>Venue Utilization</div>
          <div className="vaCardValue">{clampNumber(metrics.venueUtilization.valuePct).toFixed(1)}%</div>

          <div style={styles.deltaRow(utilUp)}>
            <ArrowIcon up={utilUp} />
            {formatPct(metrics.venueUtilization.deltaPctPoints)}
          </div>
        </article>

        {/* New Customers */}
        <article style={styles.card} className="vaCard">
          <div style={styles.cardLabel}>New Customers</div>
          <div className="vaCardValue">{formatNumber(metrics.newCustomers.value)}</div>

          <div style={styles.deltaRow(newCustomersUp)}>
            <ArrowIcon up={newCustomersUp} />
            {formatPct(metrics.newCustomers.deltaPct)}
          </div>
        </article>
      </section>

      {/* ROW 2 */}
      <section style={styles.gridRow2} className="vaRow2">
        {/* Revenue Overview */}
        <article style={styles.card} className="vaCard">
          <div style={styles.cardTitleRow}>
            <div>
              <h3 style={styles.cardTitle}>Revenue Overview</h3>
              <div style={styles.cardSub}>Last 7 Days</div>
            </div>
          </div>

          <div style={styles.chartWrap} className="vaChartWrap">
            {rev7.items.length === 0 ? (
              <div style={{ color: "#9ca3af", fontWeight: 700, paddingTop: 12, fontSize: 13 }}>No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rev7.items} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    style={{ fontWeight: 800, fill: "#9ca3af", fontSize: 12 }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                    formatter={(value) => [formatMoney(value, rev7.currency), "Revenue"]}
                    labelFormatter={(label, payload) => {
                      const d = payload?.[0]?.payload?.date;
                      return d ? `${label} (${d})` : label;
                    }}
                  />

                  <Bar dataKey="amount" radius={[10, 10, 10, 10]} maxBarSize={56} fill="#dff0df" />

                  <Bar
                    dataKey="amount"
                    radius={[10, 10, 10, 10]}
                    maxBarSize={56}
                    fill="transparent"
                    shape={(props) => {
                      const { x, y, width, height, index } = props;
                      const isSelected = index === maxBarIndex;
                      if (!isSelected) return null;
                      return <rect x={x} y={y} width={width} height={height} rx={10} ry={10} fill="#2f7a3c" />;
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </article>

        {/* Venue Performance */}
        <article style={styles.card} className="vaCard">
          <div>
            <h3 style={styles.cardTitle}>Venue Performance</h3>
            <div style={styles.cardSub}>Most booked venues this week</div>
          </div>

          <div style={styles.perfList}>
            {(venuePerf || []).slice(0, 4).map((row, idx) => (
              <div key={`${row.name}-${idx}`} style={styles.perfRow}>
                <div style={styles.perfTop}>
                  <div style={styles.perfName}>{row.name || "Ground"}</div>
                  <div style={styles.perfPct}>{Number(row.pct || 0).toFixed(1)}%</div>
                </div>
                <div style={styles.perfTrack}>
                  <div style={styles.perfFill(row.pct)} />
                </div>
              </div>
            ))}

            {(!venuePerf || venuePerf.length === 0) && (
              <div style={{ color: "#9ca3af", fontWeight: 700, paddingTop: 12, fontSize: 13 }}>
                No performance data yet
              </div>
            )}
          </div>
        </article>
      </section>

      {/* ROW 3 */}
      <section style={{ marginTop: 14 }}>
        <article style={styles.card} className="vaCard">
          <h3 style={styles.tableCardTitle}>Upcoming Bookings</h3>

          <div className="vaTableScroll">
            <div className="vaTableScrollContent">
              <div style={styles.tableWrap}>
                <div style={styles.tableHead}>
                  <div>DATE</div>
                  <div>TIME</div>
                  <div>CUSTOMER NAME</div>
                  <div>VENUE</div>
                  <div>STATUS</div>
                </div>

                {(upcomingRows || []).map((r, idx) => (
                  <div key={`${r.time}-${idx}`} style={styles.tableRow}>
                    <div style={styles.cellStrong}>{r.date || "-"}</div>
                    <div style={styles.cellStrong}>{r.time || "-"}</div>
                    <div style={styles.cellSoft}>{r.customerName || "-"}</div>
                    <div style={styles.cellSoft}>{r.venue || "-"}</div>
                    <div style={styles.pill(r.status)}>{r.status || "Pending"}</div>
                  </div>
                ))}

                {(!upcomingRows || upcomingRows.length === 0) && (
                  <div style={{ padding: 14, color: "#9ca3af", fontWeight: 800, fontSize: 13 }}>
                    No upcoming bookings
                  </div>
                )}
              </div>
            </div>
          </div>

        </article>
      </section>

      {loading && <div style={styles.hint}>Loading metrics…</div>}
      {!!errMsg && !loading && <div style={styles.error}>{errMsg}</div>}
    </div>
  );
};

export default Dashboard;
